import get from 'lodash.get';
import pluralize from 'pluralize';
import groupBy from 'lodash.groupby';

import { getCustomPrintMinPrice } from './custom-prints';
import { getAlbumPrintPackageTaggedItem } from '../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/helpers';
import {
  getPrintSizePrice,
  getPrintSizeServicesTotalPrice,
} from './print-product-configuration';
import { getPrintOrderServicesTotalPrice } from './print-order-configuration';
import {
  CUSTOM_PRINT_SUBTYPE,
  CUSTOM_PRINT_TYPE,
} from '../constants/pro-custom-prints';
import { PRINT_ASSET_TYPES } from '../constants/print-assets';
import {
  MIN_GALLERY_PRICE,
  MIN_PHOTO_BUNDLE_PRICE,
} from '../constants/album-folder';

/**
 * Returns the total quantity of print package items selected.
 *
 * @param {Array} items The print package items with their quantity.
 * @returns {number} The total number of print package items.
 */
const getTotalPrintPackageItems = items => {
  if (!items) {
    return 0;
  }

  return items.reduce((sum, item) => sum + (item.quantity || 0), 0);
};

/**
 *
 * @param {Object} item the print package item
 * @returns {PrintProductConfiguration} the print product configuration to use
 */
const getPrintPackageItemProductConfiguration = item => {
  if (!item.print_product_configuration) {
    return undefined;
  }

  // It's possible we've parsed the services into a simplified item set.
  // in this case, we use them instead of the actual config. services
  // because the parsed item is intended to be updated live.
  if (item.print_product_size_services) {
    return {
      ...(item.print_product_configuration ?? {}),
      print_product_size_services: {
        total: item.print_product_size_services.length,
        records: item.print_product_size_services,
      },
    };
  }

  return item.print_product_configuration;
};

/**
 * Returns the minimum price a print package costs based on the
 * print package items (and quantities) chosen.
 *
 * @param {Array} items - The print package item types chosen.
 * @param {Array} printPackageItemTypes - The print package item types with their prices.
 * @param {Array} selectedPrintServices - the print services selected
 * @param {Object} printOrderConfiguration - the print order configuration
 * @param {Array} selectedPrintOrderServices - the print order services selected
 * @returns {Number} - The minimum price.
 */
const getPrintPackageMinimumPrice = (
  items,
  printPackageItemTypes,
  selectedPrintServices = [],
  printOrderConfiguration = {},
  selectedPrintOrderServices = [],
) => {
  if (!items || !printPackageItemTypes) {
    return 0;
  }

  const itemSum = items.reduce((sum, item) => {
    // The item having a photos count means it's a Digital Photos Bundle, for
    // which the minimum price is defined.
    if (
      item.quantity > 0 &&
      (item.digital_bundle_data || item.digital_bundle)
    ) {
      return sum + MIN_PHOTO_BUNDLE_PRICE;
    }

    if (item.quantity > 0 && item.grants_purchased_identity) {
      return sum + MIN_GALLERY_PRICE;
    }
    // Specialty Items' min price can be overriden in packages by the PROs
    // as they see fit. A min price of 0$ has been decided for these products.

    if (
      item.custom_print &&
      item.custom_print.type === CUSTOM_PRINT_TYPE.CUSTOM_PRINT &&
      item.custom_print.subtype === CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
    ) {
      return sum;
    }
    if (item.custom_print) {
      const minPrice = getCustomPrintMinPrice(item.custom_print);
      return sum + minPrice * item.quantity;
    }

    if (item.print_product_configuration) {
      const configuration = getPrintPackageItemProductConfiguration(item);
      const printSizeServicesTotalPrice = getPrintSizeServicesTotalPrice(
        configuration,
        selectedPrintServices,
      );

      return (
        sum +
        printSizeServicesTotalPrice * item.quantity +
        getPrintSizePrice(configuration) * item.quantity
      );
    }

    const printPackageItemType = printPackageItemTypes.find(
      ({ id }) => id === item.id,
    );

    if (!printPackageItemType) {
      return sum;
    }

    if (item.is_optional) {
      return sum;
    }

    return sum + printPackageItemType.min_price.value * item.quantity;
  }, 0);

  return (
    itemSum +
    getPrintOrderServicesTotalPrice(
      printOrderConfiguration,
      selectedPrintOrderServices,
    )
  );
};

/**
 * Returns a sorted list based on Ops spec of the print package items.
 *
 * @param {Array<PrintPackageItem>} printPackageItems List of print package items to sort.
 * @param {String} sortKey Key to sort by.
 */
const sortPrintPackageItems = (printPackageItems, sortKey = 'type.name') => {
  const sortedTypes = [
    'Memory Mate',
    'Memory Mates',
    '8x10',
    '8x10 Primary Team Photo',
    '5x7',
    '5x7 Primary Team Photo',
    '4x6',
    '8 Wallets',
    '4 Wallets',
  ];

  return [...printPackageItems].sort((a, b) => {
    const indexA = sortedTypes.indexOf(get(a, sortKey));
    const indexB = sortedTypes.indexOf(get(b, sortKey));

    // Sort by name if any of the 2 is simply not found
    if (indexA === -1 || indexB === -1) {
      return get(a, sortKey, '').localeCompare(get(b, sortKey, ''));
    }

    return indexA - indexB;
  });
};

/**
 * Returns the display name of a print package item based on their type or custom print.
 *
 * @param {Object} printPackageItem The PrintPackageItem.
 * @param {Boolean} [plural] Whether to pluralize the label based on quantity.
 */
const getPrintPackageItemLabel = (printPackageItem, plural) => {
  const { print_product_configuration: printProductConfig } = printPackageItem;
  const label = (() => {
    if (printPackageItem?.grants_purchased_identity) {
      return 'Digital Photo Pass';
    }

    if (printPackageItem?.digital_bundle) {
      return `Digital Photo Bundle: ${pluralize(
        'Photo',
        printPackageItem?.digital_bundle?.photos_count ?? 1,
        true,
      )}`;
    }

    return (
      printPackageItem.type?.name ??
      printPackageItem.custom_print?.name ??
      `${printProductConfig?.print_product?.name ?? ''}${
        printProductConfig?.print_product_size
          ? ` - ${printProductConfig.print_product_size.print_size.name}`
          : ''
      }`
    );
  })();

  if (plural) {
    // Do not pluralize if the label is something like 8x10 or 4x6 etc.
    if (label?.split('x')?.length > 1) {
      return label;
    }

    return label ? pluralize(label, printPackageItem.quantity) : '';
  }

  return label ?? '';
};

/**
 * Returns whether or not a print package includes/requires custom print configuration.
 *
 * @param {Object} printPackage
 */
const requireCustomPrintConfiguration = printPackage => {
  if (!printPackage) {
    return false;
  }
  const includesCustomPrint = !!(printPackage.items?.records ?? []).find(
    printPackageItem => !!printPackageItem.custom_print,
  );
  return includesCustomPrint;
};

/**
 * Returns first custom print found on a Print package. Generally speaking, we expect 1 at most,
 * but the API will allow multiple, so this method returns the 1st one it finds.
 *
 * @param {Object} printPackage Print package to get custom print from.
 */
const getPrintPackageCustomPrint = printPackage =>
  (printPackage?.items?.records ?? []).find(item => !!item.custom_print)
    ?.custom_print;

/**
 * Determines if a custom print requires configuration. Should be the case for most here
 * but not for specialty prints, where single-image specialty prints would only need
 * the photo configured.
 *
 * @param {Object} customPrint - the custom print
 * @param {Boolean} allowMultiPose - Whether or not the event allows multi pose
 * @returns {boolean}
 */
const customPrintRequiresConfiguration = (
  customPrint,
  allowMultiPose = false,
) => {
  if (!customPrint) {
    return false;
  }
  // Assume any print outside of this function requires config.
  if (customPrint?.subtype !== CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT) {
    return true;
  }

  return allowMultiPose || customPrint?.photos_no > 1;
};
/**
 * Returns whether or not a print package requires confirgutation.
 *
 * @param {Object} printPackage Print package to get custom print from.
 * @param {Boolean} allowMultiPose - Whether or not the event allows multi pose
 */
const printPackageRequiresConfiguration = (
  printPackage,
  allowMultiPose = false,
) =>
  !!(printPackage?.items?.records ?? []).find(
    item =>
      !!customPrintRequiresConfiguration(item.custom_print, allowMultiPose) ||
      !!item.print_product_configuration,
  );

/**
 * Returns a list of configurable items within a print package.
 *
 * @param {Object} albumPrintPackage The Album Print package to get items from.
 * @param {Boolean} allowMultiPose - Whether or not the event allows multi pose
 */
const getPrintPackageConfigurableItems = (
  albumPrintPackage,
  allowMultiPose = false,
) => {
  const printPackage = albumPrintPackage?.print_package;
  const overriddenPrintPackageItem = getAlbumPrintPackageTaggedItem(
    albumPrintPackage,
  );

  return (printPackage?.items?.records ?? []).filter(
    item =>
      item.id !== overriddenPrintPackageItem?.id &&
      (!!customPrintRequiresConfiguration(item.custom_print, allowMultiPose) ||
        !!item.print_product_configuration),
  );
};

/**
 * Returns a list of non-configurable custom prints belonging to the print package
 *
 * @param {Object} albumPrintPackage The Album Print Package to get items from.
 * @param {Boolean} allowMultiPose - Whether or not the event allows multi pose
 * @returns {*[]}
 */
const getPrintPackageSpecialtyPrintsWithoutConfiguration = (
  albumPrintPackage,
  allowMultiPose = false,
) => {
  const printPackage = albumPrintPackage?.print_package;

  if (!printPackage) {
    return [];
  }

  return (printPackage?.items?.records ?? []).filter(item => {
    if (
      !item.custom_print ||
      item?.custom_print?.subtype !== CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
    ) {
      return false;
    }

    return !allowMultiPose && item?.custom_print?.photos_no === 1;
  });
};

/**
 * Returns a list of print backgrounds for a given print package
 *
 * @param {Object} albumPrintPackage The Album Print Package to get print backgrounds from.
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup to get print backgrounds from.
 * @param {Array} jerseyColors the jersey colors used by the active identity
 * @returns {*[]}
 */
const getPrintPackagePrintBackgrounds = (
  albumPrintPackage,
  albumMembershipGroup,
  jerseyColors = [],
) => {
  const albumFolder =
    albumMembershipGroup?.album?.root_album_folder ??
    albumMembershipGroup?.album_folder;

  if (albumFolder?.print_assets?.records?.length > 0) {
    const assets = (albumFolder?.print_assets?.records ?? [])
      .filter(
        ({ print_asset: printAsset }) =>
          printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
      )
      .map(printPackagePrintAsset => printPackagePrintAsset.print_asset);

    if (jerseyColors.length > 0) {
      return assets.filter(
        ({ jersey_colors: assetColors }) =>
          !assetColors?.records?.length ||
          !!assetColors?.records?.find(({ jersey_color: color }) =>
            jerseyColors.includes(color),
          ),
      );
    }

    return assets;
  }

  const printPackage = albumPrintPackage?.print_package;

  if (!printPackage) {
    return [];
  }

  const assets = (printPackage?.print_assets?.records ?? [])
    .filter(
      ({ print_asset: printAsset }) =>
        printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
    )
    .map(printPackagePrintAsset => printPackagePrintAsset.print_asset);

  if (jerseyColors.length > 0) {
    return assets.filter(
      ({ jersey_colors: assetColors }) =>
        !assetColors?.records?.length ||
        !!assetColors?.records?.find(({ jersey_color: color }) =>
          jerseyColors.includes(color),
        ),
    );
  }

  return assets;
};

/**
 * Given a list of print package items, returns the groups for it based off current requirements (quantity + printPackageSize match)
 *
 * @param {Array} items the print package items.
 * @returns {Object}
 */
const getPrintPackageGroupedItems = items => {
  return groupBy(items, item => {
    if (item?.print_product_configuration?.print_product_size) {
      return `${item.quantity}${item.print_product_configuration.print_product_size.id}`;
    }

    if (item?.custom_print?.id) {
      return `${item.quantity}${item.custom_print.id}`;
    }

    return item.id;
  });
};

export {
  customPrintRequiresConfiguration,
  getTotalPrintPackageItems,
  getPrintPackageItemProductConfiguration,
  getPrintPackageMinimumPrice,
  sortPrintPackageItems,
  getPrintPackageItemLabel,
  requireCustomPrintConfiguration,
  getPrintPackageCustomPrint,
  printPackageRequiresConfiguration,
  getPrintPackageConfigurableItems,
  getPrintPackageSpecialtyPrintsWithoutConfiguration,
  getPrintPackagePrintBackgrounds,
  getPrintPackageGroupedItems,
};
